import { optimizelyInstance } from "./optimizelyInstance";
import { Flags } from "./flags";

/**
 * Pre-auth gate for direct-region (EU data residency) authentication.
 *
 * Every EU code path runs BEFORE authentication — the module-scope OktaAuth build,
 * RequireAuth's redirect decision, and the /login-region route — so `useFeatureFlag`
 * cannot reach it: that hook needs the OptimizelyProvider, which lives inside
 * UserProvider, which renders inside <Security> and builds its user context from a
 * token-derived profile. Circular.
 *
 * Instead the flag is decided anonymously via the imperative client and latched in
 * sessionStorage, so the synchronous module scope can read it across the hard reloads
 * this flow performs (broker -> /login-region -> "/").
 */
export const EU_FLAG_CACHE_KEY = "optiid:eu:enabled";

/** How long to wait for the datafile before giving up and using the latch. */
const ON_READY_TIMEOUT_MS = 2000;

/**
 * Attempts before falling back. A first-visit user has no latch, so a single transient
 * datafile failure would decide their region — retry once rather than let one CDN blip
 * route a (post-rollout) EU user down the NA flow.
 *
 * Deliberately NOT fail-closed: with enable_eu at 0% rollout every user is NA, so
 * refusing to resolve would turn a flag-service hiccup into a total login outage for
 * everyone, to protect a cohort that does not exist yet. And it cannot be narrowed to
 * EU users — knowing who is EU is exactly what the flag plus the broker are for.
 */
const ON_READY_ATTEMPTS = 2;

/** Anonymous pre-auth identity — no profile exists yet. */
const ANONYMOUS_USER = { id: "anonymous", attributes: {} };

/** In-flight/settled resolution, so repeated callers decide once. */
let pending: Promise<boolean> | null = null;

function readLatch(): string | null {
    try {
        return window.sessionStorage.getItem(EU_FLAG_CACHE_KEY);
    } catch {
        return null;
    }
}

/**
 * Synchronous read of the latched decision. Safe at module scope, which is the whole
 * point — OktaAuth must be constructed before any promise can settle.
 */
export function isEuEnabledCached(): boolean {
    return readLatch() === "true";
}

/**
 * Resolve `enable_eu` anonymously and latch the result.
 *
 * On failure (no datafile, timeout, SDK error) an EXISTING latch wins: flipping a live
 * EU session to false would rebuild OktaAuth against the NA issuer while an EU token
 * sits in storage, which breaks the session. Only default to false when nothing is
 * latched yet.
 */
export function resolveEuEnabled(): Promise<boolean> {
    if (!pending) {
        pending = decide();
    }
    return pending;
}

/** Wait for the datafile, retrying so one transient failure does not decide the region. */
async function awaitDatafile(): Promise<boolean> {
    for (let attempt = 0; attempt < ON_READY_ATTEMPTS; attempt++) {
        const ready = await optimizelyInstance.onReady({ timeout: ON_READY_TIMEOUT_MS });
        if (ready?.success) {
            return true;
        }
    }
    return false;
}

async function decide(): Promise<boolean> {
    try {
        // setUser must come first: the SDK's onReady races a timeout against
        // Promise.all([userReadyPromise, clientReadyPromise]), and userReadyPromise
        // only settles via setUser. Without it onReady always times out.
        await optimizelyInstance.setUser(ANONYMOUS_USER);
        if (!(await awaitDatafile())) {
            return isEuEnabledCached();
        }

        const enabled = optimizelyInstance.decide(Flags.ENABLE_EU)?.enabled;
        window.sessionStorage.setItem(EU_FLAG_CACHE_KEY, String(enabled));
        return enabled;
    } catch {
        return isEuEnabledCached();
    }
}

/** Test seam — drops the memoized decision so each test resolves fresh. */
export function resetEuFlagForTests(): void {
    pending = null;
}
